/**
 * @file Note de service affichée au client à l'étape du choix de l'horaire.
 * @module packages.common.components.table-reservation.components.ServiceNote
 *
 * Rend le contenu HTML enrichi de la note (gras, listes, liens) de façon
 * sécurisée (DOMPurify) dans une carte sobre, alignée sur le rendu du message
 * de bienvenue. Ne s'affiche pas si la note est vide une fois les balises retirées.
 */

import React from 'react';
import PropTypes from 'prop-types';
import DOMPurify from 'dompurify';
import {Box} from '@mui/material';
import {typography} from 'packages/common/theme/typography';

/**
 * Détermine si un contenu HTML possède du texte visible.
 *
 * @param {string} html - Contenu HTML
 * @return {boolean} Vrai si du texte subsiste une fois les balises retirées
 */
function hasVisibleText(html) {
  if (!html) return false;
  return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim() !== '';
}

/**
 * Carte de note de service (contenu HTML enrichi).
 *
 * @param {Object} props
 * @param {string} props.note - Contenu HTML déjà résolu dans la langue courante
 * @return {JSX.Element|null}
 */
export default function ServiceNote({note}) {
  if (!hasVisibleText(note)) {
    return null;
  }

  return (
    <Box
      sx={{
        mb: 2.5,
        px: 2,
        py: 1.5,
        borderRadius: 2,
        backgroundColor: 'grey.50',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box
        sx={{
          'color': 'text.secondary',
          'lineHeight': 1.5,
          'fontSize': typography.body2.fontSize,
          'fontWeight': 400,
          'wordBreak': 'break-word',
          '& p': {margin: 0, marginBottom: 0.25, fontSize: typography.body2.fontSize, lineHeight: 1.5},
          '& p:last-child': {marginBottom: 0},
          '& strong, & b': {fontWeight: 600, color: 'text.primary'},
          '& em, & i': {fontStyle: 'italic'},
          '& u': {textDecoration: 'underline'},
          '& ul, & ol': {margin: 0, marginBottom: 0.25, paddingLeft: 2.5},
          '& ul:last-child, & ol:last-child': {marginBottom: 0},
          '& li': {fontSize: typography.body2.fontSize, lineHeight: 1.5},
          '& a': {color: 'primary.main', textDecoration: 'underline'},
        }}
        dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(note)}}
      />
    </Box>
  );
}

ServiceNote.propTypes = {
  note: PropTypes.string,
};
